import { addAnswer, addPrefilledAnswers, clearAnswers } from '../answers-slice'
import {
  AllSteps,
  Answer,
  AnswerId,
  Answers,
  ProviderSlugs,
  ProviderType,
  Scenario,
  ScenarioStep,
  ScenarioStepModifiableElements,
  ScenarioType,
  StepId,
  Steps,
  Translatable,
} from '../interfaces'
import { call, delay, put, select, takeEvery } from 'redux-saga/effects'
import {
  selectAnswers,
  selectCurrentStep,
  selectCurrentStepId,
  selectIsCurrentStepPrefilled,
  selectNextStepId,
  selectPrefilledAnswers,
  selectReferrerUrl,
  selectScenario,
  selectScenarioType,
} from '../selectors'
import { gotoStep, nextStep, NextStepPayload } from '../step-slice'
import { finishScenario, setIsScenarioLoading, setIsScenarioWaitingForAnswer, setScenario } from '../scenario-slice'
import { createToast } from '../../components/toast'
import { refreshPhonesSync } from './mobile'
import { RootState } from '../../app/state'
import {
  clearScenarioCookie,
  getCookie,
  parseScenario,
  setAdbannerTargetingKeyValueCookie,
  setCurrentProvider,
  setInternetSpeedSearched,
} from '../../scenarios'
import { setCookie } from '../../scenarios'
import i18next from 'i18next'
import { updateScenario } from '../../state'
import { getProviderTypeFromScenario } from '../../scenarios/utils'
import {
  checkProviderAvailability,
  sendAlertRequest,
  sendLeadRequest,
  sendNewsletterRequest,
  sendReviewsRequest,
  sendNewTicketRequest,
} from '../../connectivity/api'
import { getLocale } from '../../i18n/locale'

export function* answerSaga() {
  yield takeEvery(addAnswer.type, answerAdded)
}

export function* answerAdded(action: ReturnType<typeof addAnswer>) {
  yield put(setIsScenarioWaitingForAnswer(true))
  const currentStep: ScenarioStep = yield select(selectCurrentStep)
  const scenarioType: ScenarioType = yield select(selectScenarioType)
  const currentStepId: Steps = yield select(selectCurrentStepId)
  const { answerIds, stepId, hidden } = action.payload
  let hasTemporaryNextStep = false
  let previousNextStep = null
  let isProvinceStep = false

  if (currentStep) {
    if (currentStep.clears) {
      yield put(clearAnswers({ ids: currentStep.clears }))
    }

    yield call(autoFillFutureAnswers, currentStep.answers, answerIds)

    if (currentStep.id === Steps.PROVINCE) {
      setCookie(Steps.PROVINCE, answerIds[0])
      setCookie('stepQ', 'yes')
      isProvinceStep = true
    }

    if (currentStep.id === Steps.BIG_ENTREPRISE) {
      setCookie(Steps.BIG_ENTREPRISE, answerIds[0])
    }

    if (currentStep.id === Steps.CURRENT_PROVIDER) {
      setCurrentProvider({ providerSlug: answerIds[0], type: scenarioType })
    }

    if (
      currentStep.id === Steps.INTERNET_SPEED ||
      currentStep.id === Steps.SLIDER_DATA_AMOUNT ||
      stepId === Steps.DOWNLOAD_SPEED
    ) {
      setInternetSpeedSearched({ speed: answerIds[0] })
    }

    if (currentStep.id === Steps.BIG_ENTREPRISE) {
      setAdbannerTargetingKeyValueCookie({ key: 'GovEmployee', value: answerIds[0] })
    }

    if (currentStep.id === Steps.ARE_YOU_MOVING) {
      setAdbannerTargetingKeyValueCookie({ key: 'moveout', value: answerIds[0] })
    }

    if (currentStep.id === Steps.INTERNET_USAGE) {
      const answers: Answers = yield select(selectAnswers)

      if (answers[currentStepId]?.includes('finishScenario')) {
        yield put(
          updateScenario({
            stepToUpdate: currentStepId,
            value: Steps.DONE,
            element: ScenarioStepModifiableElements.NEXT,
          }),
        )
      }
    }

    if (action.payload.stepId === Steps.PHONE_MODEL_ID) {
      previousNextStep = currentStep.next
      yield put(
        updateScenario({
          stepToUpdate: currentStepId,
          value: Steps.EMAIL_ADDRESS,
          element: ScenarioStepModifiableElements.NEXT,
        }),
      )
      hasTemporaryNextStep = true
    }

    if (action.payload.stepId === Steps.SLIDER_DATA) {
      const answers: Answers = yield select(selectAnswers)
      if (answers && answers['slider-data'] && parseInt(answers['slider-data'][0]) <= 20000) {
        previousNextStep = currentStep.next
        yield put(
          updateScenario({
            stepToUpdate: currentStepId,
            value: Steps.BUY_OR_BRING_PHONE,
            element: ScenarioStepModifiableElements.NEXT,
          }),
        )
        hasTemporaryNextStep = true
      }
    }

    if (!hidden) {
      yield call(proceedToNextStep)
    }

    if (hasTemporaryNextStep && previousNextStep) {
      yield put(
        updateScenario({
          stepToUpdate: currentStepId,
          value: previousNextStep,
          element: ScenarioStepModifiableElements.NEXT,
        }),
      )
      hasTemporaryNextStep = false
    }

    if (currentStep.id !== Steps.EMAIL_ADDRESS) {
      yield put(setIsScenarioWaitingForAnswer(false))
    } else {
      yield delay(3000) // to allow cio do its thing
      yield put(setIsScenarioWaitingForAnswer(false))
    }

    if (isProvinceStep) {
      yield call(refreshPhonesWithinCurrentScenario)
    }
  }
}

function* refreshPhonesWithinCurrentScenario() {
  const scenarioType: ScenarioType = yield select(selectScenarioType)

  yield put(setIsScenarioLoading())
  if (
    scenarioType === ScenarioType.MOBILE ||
    scenarioType === ScenarioType.MOBILE_SEARCH_EMAIL ||
    scenarioType === ScenarioType.MOBILE_ALERT
  ) {
    yield call(refreshPhonesSync)
  }

  const state: RootState = yield select()
  const scenario: Scenario = yield select(selectScenario)
  const newScenario: Scenario = yield call(parseScenario, { scenario, state })
  yield put(setScenario(newScenario))
  yield put(setIsScenarioLoading(false))
}

function* autoFillFutureAnswers(stepAnswers: Answer[], currentAnswers: AnswerId[]) {
  for (const currentAnswer of currentAnswers) {
    const answer = stepAnswers.find((stepAnswer) => stepAnswer.id === currentAnswer)

    if (answer && answer.autoFillAnswers) {
      yield put(addPrefilledAnswers(answer.autoFillAnswers))
    }
  }
}

export function* proceedToNextStep(): unknown {
  const nextStepId: StepId | null = yield select(selectNextStepId)
  const scenario: Scenario = yield select(selectScenario)
  const providerSlugsThatNeedAddressAvailabilityStep = [ProviderSlugs.FIBRESTREAM]

  if (!nextStepId) {
    yield call(createToast, 'Unable to determine next step', 'error')
  }

  if (nextStepId === Steps.START_RATING) {
    const providerName: string | undefined = getCookie('providerNameReviews')
    const providerType: ProviderType = getProviderTypeFromScenario(scenario)

    yield put(
      updateScenario({
        stepToUpdate: Steps.START_RATING,
        value: i18next.t('start-rating_question', {
          providerName,
          providerType,
        }) as Translatable<`${AllSteps}_question`>,
        element: ScenarioStepModifiableElements.QUESTION,
      }),
    )
  }

  if (nextStepId === Steps.CONFIRM_LEAD) {
    const providerName: string | undefined = getCookie('providerName')

    yield put(
      updateScenario({
        stepToUpdate: Steps.CONFIRM_LEAD,
        value: i18next.t('confirm-lead_answerPlaceholder', {
          providerName,
        }) as Translatable<`${AllSteps}_answerPlaceholder`>,
        element: ScenarioStepModifiableElements.ANSWER_PLACEHOLDER,
      }),
    )
  }

  if (nextStepId === Steps.THANKS) {
    const answers: Answers = yield select(selectAnswers)
    switch (scenario.type) {
      case ScenarioType.MOBILE_REVIEWS:
      case ScenarioType.INTERNET_REVIEWS:
        yield call(sendReviews, answers)
        break
      case ScenarioType.INTERNET_ALERT:
      case ScenarioType.MOBILE_ALERT:
      case ScenarioType.INTERNET_ALERT_OBSERVER:
      case ScenarioType.MOBILE_ALERT_OBSERVER:
      case ScenarioType.COMPETITION_INTERNET_ALERT:
        yield call(sendAlert, answers)
        break
      case ScenarioType.LEAD_MOBILE:
      case ScenarioType.LEAD_INTERNET:
        yield call(sendLead, answers)
        break
      case ScenarioType.NEWSLETTER:
        yield call(sendNewsletter, answers)
        break
      case ScenarioType.TICKET:
        yield call(sendNewTicket, answers)
        break
      default:
        console.warn('No action')
    }
  }

  if (nextStepId === Steps.PROVIDER_AVAILABILITY) {
    try {
      const providerSlug = JSON.parse(getCookie('scenario')).initialInformation?.providerSlug
      // const availability = yield call(checkProviderAvailability, providerSlug)

      if (providerSlugsThatNeedAddressAvailabilityStep.includes(providerSlug)) {
        const availability = yield call(checkProviderAvailability, providerSlug) // Temporarily to only be used by fibrstream
        yield put(
          addAnswer({
            stepId: Steps.PROVIDER_AVAILABILITY,
            answerIds: [availability],
            hidden: true,
            prefilled: false,
          }),
        )
      } else {
        // Temporarily changed to avoid slow call to api
        // yield put(
        //   addAnswer({
        //     stepId: Steps.PROVIDER_AVAILABILITY,
        //     answerIds: ['continue-with-provider', availability],
        //     hidden: true,
        //     prefilled: true,
        //   }),
        // )

        // Must use the the api when fixed proble of speed
        yield put(
          addAnswer({
            stepId: Steps.PROVIDER_AVAILABILITY,
            answerIds: ['continue-with-provider', 'false'],
            hidden: true,
            prefilled: true,
          }),
        )
      }
    } catch (error) {
      console.log('There is some error')
    }
  }

  if (nextStepId === Steps.DONE) {
    if (scenario.type === ScenarioType.DECISION) {
      const referrer: string = yield select(selectReferrerUrl)
      let answers: Answers = yield select(selectAnswers)
      const { protocol, host, pathname } = window.location
      const scenarioType = answers['decision-mobile-internet'] ? answers['decision-mobile-internet']![0] : 'mobile'
      const locale = getLocale()
      answers = {}
      clearScenarioCookie()
      window.location.assign(
        `${protocol}//${host}${pathname}?locale=${locale}&scenarioType=${scenarioType}&referrer=${referrer}`,
      )
    } else {
      if (scenario.type === ScenarioType.MOBILE_SEARCH_EMAIL || scenario.id === 'mobile-d') {
        const answers: Answers = yield select(selectAnswers)
        if (answers && answers['email-address'] && answers['email-address'][0]) {
          yield call(sendAlert, answers)
        }
      }
      yield put(finishScenario())
    }
  } else if (nextStepId) {
    const prefilledAnswers: Answers = yield select(selectPrefilledAnswers)
    const isCurStepPrefilled: boolean = yield select(selectIsCurrentStepPrefilled)

    yield put(nextStep({ id: nextStepId, disablePersistence: isCurStepPrefilled } as NextStepPayload))

    if (prefilledAnswers[nextStepId]) {
      yield call(answerAdded, addAnswer({ stepId: nextStepId, answerIds: prefilledAnswers[nextStepId] as string[] }))
    }
  }

  if (nextStepId === Steps.BUY_OR_BRING_PHONE) {
    if (scenario.type === ScenarioType.MOBILE_ALERT) {
      const answers: Answers = yield select(selectAnswers)

      if (answers && answers['phone-model-id'] && answers['phone-model-id'][0] !== '2') {
        yield put(gotoStep({ id: Steps.EMAIL_ADDRESS }))
      }
    }
  }
}

function* sendReviews(answers: Answers) {
  const scenarioType: ScenarioType = yield select(selectScenarioType)
  sendReviewsRequest(answers, scenarioType)
}

function* sendAlert(answers: Answers) {
  const scenarioType: ScenarioType = yield select(selectScenarioType)
  sendAlertRequest(answers, scenarioType)
}

function* sendLead(answers: Answers) {
  const scenarioType: ScenarioType = yield select(selectScenarioType)
  sendLeadRequest(answers, scenarioType)
}

function* sendNewsletter(answers: Answers) {
  const scenarioType: ScenarioType = yield select(selectScenarioType)
  sendNewsletterRequest(answers, scenarioType)
}

function* sendNewTicket(answers: Answers) {
  const scenarioType: ScenarioType = yield select(selectScenarioType)
  sendNewTicketRequest(answers, scenarioType)
}
